import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useRender } from '@base-ui/react/use-render';
import { cn } from '../../lib/utils';

/**
 * Button — shadcn (base-vega) variant on Base UI.
 *
 * Migration note (2026-04-29): Replaced Radix `Slot` + `asChild` with Base UI's
 * `useRender` hook so callers can pass `render={<a href="…" />}` to compose.
 * The previous `asChild` boolean is gone — passing a child element via `render`
 * does the same thing without the runtime fragility of cloneElement.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-[var(--color-primary-disabled)] disabled:text-[var(--color-primary-disabled-fg)] disabled:opacity-100',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        success: 'bg-success text-success-foreground hover:bg-success/90',
        warning: 'bg-warning text-warning-foreground hover:bg-warning/90',
        outline: 'border border-input bg-background hover:bg-muted',
        'destructive-outline':
          'border border-destructive/50 bg-background text-destructive hover:bg-destructive/10',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-muted',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-md px-6 text-base',
        xl: 'h-14 rounded-lg px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  /**
   * Compose with another element (replaces Radix `asChild`).
   * Pass a React element to merge button props onto, e.g.
   * `<Button render={<Link to="/x" />}>Go</Button>`.
   */
  render?: React.ReactElement;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, render, type = 'button', ...props }, ref) => {
    return useRender<Record<string, unknown>, HTMLButtonElement, true>({
      render: render ?? <button type={type} />,
      ref,
      props: {
        className: cn(buttonVariants({ variant, size, className })),
        type,
        ...props,
      },
      defaultTagName: 'button',
    });
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
